<template>
<div class="tp-root" :style="themePageStyle">
  <nav class="tp-topbar"><div class="container d-flex align-items-center py-2">
    <a href="javascript:void(0)" @click="$router.go(-1)" class="tp-back"><i class="mdi mdi-arrow-left"></i></a>
    <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" class="me-3" />
    <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Transporter Profile</span>
  </div></nav>

  <div class="container tp-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!profile || !profile.user_id" class="text-center py-5">
      <i class="mdi mdi-truck-remove" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Transporter Not Found</h5>
      <button class="btn btn-sm btn-outline-warning mt-3" @click="$router.push('/transport')">Back to Directory</button>
    </div>

    <div v-else class="row g-4">
      <!-- Left: main profile -->
      <div class="col-lg-8">
        <div class="tp-card mb-3">
          <div class="d-flex align-items-start gap-3">
            <div class="tp-avatar"><img v-if="profile.avatar_url" :src="profile.avatar_url" /><span v-else>{{ (profile.company_name || profile.transporter_name || 'T').charAt(0) }}</span></div>
            <div class="flex-fill" style="min-width:0;">
              <h4 class="fw-bold mb-1" style="color:var(--fm-h,#F5F0E8);">{{ profile.company_name || profile.transporter_name }}</h4>
              <div v-if="profile.company_name && profile.transporter_name" style="color:var(--fm-m,#A09888);font-size:13px;">Operated by {{ profile.transporter_name }}</div>
              <div v-if="profile.ntsa_compliance_number" class="mt-1" style="color:var(--fm-s,#6B6356);font-size:12px;">
                <span v-if="String(profile.ntsa_verified)==='1'" style="color:#50C878;"><i class="mdi mdi-check-decagram me-1"></i>NTSA Verified · </span>
                {{ profile.ntsa_compliance_number }}
              </div>
              <div v-if="profile.status!=='active'" class="mt-2"><span class="tp-tag tp-tag-red">Currently inactive</span></div>
            </div>
            <div v-if="profile.avg_rating" class="text-end">
              <div style="font-size:28px;font-weight:700;color:#FFD700;">{{ Number(profile.avg_rating).toFixed(1) }}</div>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ profile.total_reviews||0 }} reviews</div>
            </div>
          </div>
        </div>

        <!-- Bio -->
        <div v-if="profile.bio" class="tp-card mb-3">
          <h6 class="tp-section-title">About</h6>
          <p style="color:var(--fm-m,#A09888);font-size:13px;line-height:1.7;margin:0;">{{ profile.bio }}</p>
        </div>

        <!-- Animal types served -->
        <div v-if="animalTypes.length" class="tp-card mb-3">
          <h6 class="tp-section-title">Animals Carried</h6>
          <div class="d-flex flex-wrap gap-2">
            <span v-for="a in animalTypes" :key="a" class="tp-spec">{{ a.replace(/_/g,' ') }}</span>
          </div>
        </div>

        <!-- Capacity -->
        <div v-if="profile.max_capacity_large || profile.max_capacity_small || profile.max_capacity_poultry" class="tp-card mb-3">
          <h6 class="tp-section-title">Fleet Capacity</h6>
          <div class="row g-2">
            <div class="col-6 col-md-4" v-if="profile.max_capacity_large">
              <div class="tp-cap"><div class="tp-cap-val">{{ profile.max_capacity_large }}</div><div class="tp-cap-lbl">Cattle / large stock</div></div>
            </div>
            <div class="col-6 col-md-4" v-if="profile.max_capacity_small">
              <div class="tp-cap"><div class="tp-cap-val">{{ profile.max_capacity_small }}</div><div class="tp-cap-lbl">Goats / sheep</div></div>
            </div>
            <div class="col-6 col-md-4" v-if="profile.max_capacity_poultry">
              <div class="tp-cap"><div class="tp-cap-val">{{ profile.max_capacity_poultry }}</div><div class="tp-cap-lbl">Poultry</div></div>
            </div>
          </div>
        </div>

        <!-- Vehicles -->
        <div v-if="vehicles.length" class="tp-card mb-3">
          <h6 class="tp-section-title">Fleet ({{ vehicles.length }})</h6>
          <div v-for="v in vehicles" :key="v.id" class="tp-vehicle">
            <div class="tp-vehicle-icon"><i class="mdi mdi-truck"></i></div>
            <div class="flex-fill">
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;text-transform:capitalize;">
                {{ v.vehicle_type.replace(/_/g,' ') }}
                <span v-if="v.make || v.model" style="color:var(--fm-m,#A09888);font-weight:400;">— {{ v.make }} {{ v.model }}</span>
              </div>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;">
                <span class="tp-ref">{{ v.registration_number }}</span>
                <span v-if="v.capacity_description" class="ms-2">· {{ v.capacity_description }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Insurance (only if provided) -->
        <div v-if="profile.insurance_provider" class="tp-card mb-3">
          <h6 class="tp-section-title">Transit Insurance</h6>
          <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ profile.insurance_provider }}</div>
          <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:4px;">
            <span v-if="profile.insurance_policy_number">Policy: {{ profile.insurance_policy_number }}</span>
            <span v-if="profile.insurance_expiry" class="ms-2">· Valid until {{ profile.insurance_expiry }}</span>
          </div>
        </div>
      </div>

      <!-- Right: sticky sidebar -->
      <div class="col-lg-4">
        <div class="tp-card" style="position:sticky;top:80px;">
          <h6 class="tp-section-title">Track Record</h6>
          <div class="d-flex justify-content-between mb-2"><span style="color:var(--fm-s,#6B6356);font-size:12px;">Total trips</span><span style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ profile.total_trips||0 }}</span></div>
          <div class="d-flex justify-content-between mb-2" v-if="profile.on_time_delivery_pct"><span style="color:var(--fm-s,#6B6356);font-size:12px;">On-time delivery</span><span style="color:#50C878;font-weight:600;">{{ Number(profile.on_time_delivery_pct).toFixed(0) }}%</span></div>
          <div class="d-flex justify-content-between mb-2"><span style="color:var(--fm-s,#6B6356);font-size:12px;">Service radius</span><span style="color:var(--fm-h,#F5F0E8);">{{ profile.service_radius_km||'—' }} km</span></div>

          <div class="mt-3 pt-3" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <h6 class="tp-section-title">Contact</h6>
            <div v-if="profile.phone" style="color:var(--fm-h,#F5F0E8);font-size:13px;"><i class="mdi mdi-phone me-1" style="color:#BFA164;"></i>{{ profile.phone }}</div>
            <div v-if="profile.email" style="color:var(--fm-m,#A09888);font-size:12px;margin-top:2px;"><i class="mdi mdi-email-outline me-1"></i>{{ profile.email }}</div>
          </div>

          <div class="mt-4">
            <button class="btn btn-warning w-100" :disabled="profile.status!=='active'" @click="openBookModal">
              <i class="mdi mdi-calendar-check me-1"></i>Request Transport
            </button>
            <div v-if="profile.status!=='active'" style="font-size:11px;color:#FF9800;text-align:center;margin-top:6px;">
              <i class="mdi mdi-alert-circle-outline me-1"></i>This transporter is not accepting jobs right now
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Request Transport Modal -->
  <div v-if="showBookModal" class="tp-overlay" @click.self="closeBookModal">
    <div class="tp-modal">
      <div class="tp-modal-hd">
        <div>
          <div style="color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;">Transport Booking</div>
          <h5 style="color:var(--fm-h,#F5F0E8);margin:2px 0 0;font-size:17px;font-weight:700;">Request transport from {{ profile.company_name || profile.transporter_name }}</h5>
        </div>
        <button class="tp-x" @click="closeBookModal">&times;</button>
      </div>

      <div class="tp-modal-bd">
        <div v-if="bookError" class="tp-alert">{{ bookError }}</div>

        <!-- Cargo -->
        <h6 class="tp-sect">Cargo</h6>
        <div class="row g-2">
          <div class="col-md-4">
            <label class="tp-lbl">Animal count *</label>
            <input v-model.number="book.animalCount" type="number" min="1" class="tp-inp" placeholder="e.g. 10" />
          </div>
          <div class="col-md-8">
            <label class="tp-lbl">What animals?</label>
            <input v-model="book.animalTypeDescription" class="tp-inp" placeholder="e.g. 10 adult Friesian cows" />
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Estimated total weight (kg)</label>
            <input v-model.number="book.estimatedTotalWeightKg" type="number" min="0" step="10" class="tp-inp" placeholder="e.g. 4500" />
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Order reference (optional)</label>
            <input v-model="book.orderId" class="tp-inp" placeholder="Link to an existing order" />
          </div>
        </div>

        <!-- Pickup -->
        <h6 class="tp-sect mt-3">Pickup</h6>
        <div class="row g-2">
          <div class="col-12" style="position:relative;">
            <label class="tp-lbl">Pickup address *</label>
            <div style="display:flex;gap:8px;">
              <div style="flex:1;position:relative;">
                <input v-model="book.pickupAddress" type="text" class="tp-inp" placeholder="Search town, farm, area..." @input="onSearch('pickup')" @focus="activeSearch='pickup'" autocomplete="off" style="padding-right:36px;" />
                <i v-if="gpsLoading==='pickup'" class="mdi mdi-loading mdi-spin" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);color:var(--fm-s,#6B6356);"></i>
                <i v-else-if="book.pickupLatitude" class="mdi mdi-check-circle" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);color:#50C878;"></i>
              </div>
              <button type="button" class="tp-gps-btn" @click="useMyLocation('pickup')" :disabled="gpsLoading==='pickup'" title="Use my current location">
                <i v-if="gpsLoading==='pickup'" class="mdi mdi-loading mdi-spin"></i>
                <i v-else class="mdi mdi-crosshairs-gps"></i>
              </button>
            </div>
            <div v-if="activeSearch==='pickup' && pickupResults.length > 0" class="tp-loc-dropdown">
              <div v-for="(loc, li) in pickupResults" :key="'p'+li" class="tp-loc-item" @click="pickLocation('pickup',loc)">
                <i class="mdi mdi-map-marker" style="color:#BFA164;flex-shrink:0;margin-top:2px;"></i>
                <div><div style="font-size:13px;color:var(--fm-h,#F5F0E8);">{{ loc.display_name }}</div><div style="font-size:11px;color:var(--fm-s,#6B6356);">{{ loc.area }}</div></div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Pickup contact name</label>
            <input v-model="book.pickupContactName" class="tp-inp" placeholder="e.g. John Kamau" />
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Pickup contact phone</label>
            <input v-model="book.pickupContactPhone" class="tp-inp" placeholder="e.g. 0722 000 000" />
          </div>
        </div>

        <!-- Delivery -->
        <h6 class="tp-sect mt-3">Delivery</h6>
        <div class="row g-2">
          <div class="col-12" style="position:relative;">
            <label class="tp-lbl">Delivery address *</label>
            <div style="display:flex;gap:8px;">
              <div style="flex:1;position:relative;">
                <input v-model="book.deliveryAddress" type="text" class="tp-inp" placeholder="Search town, farm, area..." @input="onSearch('delivery')" @focus="activeSearch='delivery'" autocomplete="off" style="padding-right:36px;" />
                <i v-if="gpsLoading==='delivery'" class="mdi mdi-loading mdi-spin" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);color:var(--fm-s,#6B6356);"></i>
                <i v-else-if="book.deliveryLatitude" class="mdi mdi-check-circle" style="position:absolute;right:10px;top:50%;transform:translateY(-50%);color:#50C878;"></i>
              </div>
              <button type="button" class="tp-gps-btn" @click="useMyLocation('delivery')" :disabled="gpsLoading==='delivery'" title="Use my current location">
                <i v-if="gpsLoading==='delivery'" class="mdi mdi-loading mdi-spin"></i>
                <i v-else class="mdi mdi-crosshairs-gps"></i>
              </button>
            </div>
            <div v-if="activeSearch==='delivery' && deliveryResults.length > 0" class="tp-loc-dropdown">
              <div v-for="(loc, li) in deliveryResults" :key="'d'+li" class="tp-loc-item" @click="pickLocation('delivery',loc)">
                <i class="mdi mdi-map-marker" style="color:#BFA164;flex-shrink:0;margin-top:2px;"></i>
                <div><div style="font-size:13px;color:var(--fm-h,#F5F0E8);">{{ loc.display_name }}</div><div style="font-size:11px;color:var(--fm-s,#6B6356);">{{ loc.area }}</div></div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Delivery contact name</label>
            <input v-model="book.deliveryContactName" class="tp-inp" placeholder="e.g. Jane Wanjiku" />
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Delivery contact phone</label>
            <input v-model="book.deliveryContactPhone" class="tp-inp" placeholder="e.g. 0711 000 000" />
          </div>
        </div>

        <!-- Schedule -->
        <h6 class="tp-sect mt-3">Schedule</h6>
        <div class="row g-2">
          <div class="col-md-6">
            <label class="tp-lbl">Pickup date *</label>
            <input v-model="book.scheduledPickupDate" type="date" class="tp-inp" :min="todayISO" />
          </div>
          <div class="col-md-6">
            <label class="tp-lbl">Pickup time</label>
            <input v-model="book.scheduledPickupTime" type="time" class="tp-inp" />
          </div>
          <div class="col-12">
            <label class="tp-lbl">Special instructions</label>
            <textarea v-model="book.specialInstructions" rows="2" class="tp-inp" placeholder="e.g. Bring partitioned trailer, keep animals separated; gate is locked, call on arrival" />
          </div>
        </div>

        <div class="tp-summary mt-3">
          <div><i class="mdi mdi-information-outline me-1" style="color:#BFA164;"></i>Your request will be sent to <strong style="color:var(--fm-h,#F5F0E8);">{{ profile.company_name || profile.transporter_name }}</strong>. They will either accept and quote a price, or decline. You will be notified either way.</div>
        </div>
      </div>

      <div class="tp-modal-ft">
        <button class="btn btn-sm btn-outline-secondary" @click="closeBookModal" :disabled="submitting">Cancel</button>
        <button class="btn btn-sm btn-warning" @click="submitBooking" :disabled="submitting">
          <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="mdi mdi-send me-1"></i>
          {{ submitting ? 'Sending…' : 'Send Request' }}
        </button>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .tp-root{background:#F7F5F2!important}
html[data-theme="light"] .tp-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .tp-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.tp-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.tp-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.tp-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:12px;text-decoration:none}
.tp-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.tp-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px}
.tp-section-title{color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px}
.tp-avatar{width:72px;height:72px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:700;color:#BFA164;overflow:hidden;flex-shrink:0}
.tp-avatar img{width:100%;height:100%;object-fit:cover}
.tp-tag{font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600}
.tp-tag-red{background:rgba(255,107,107,.1);color:#FF6B6B}
.tp-spec{font-size:11px;padding:4px 10px;border-radius:14px;background:rgba(191,161,100,.1);color:#BFA164;font-weight:500;text-transform:capitalize}
.tp-cap{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:12px;text-align:center}
.tp-cap-val{color:#BFA164;font-weight:700;font-size:22px}
.tp-cap-lbl{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.5px;margin-top:2px}
.tp-vehicle{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.tp-vehicle:last-child{border-bottom:none}
.tp-vehicle-icon{width:36px;height:36px;border-radius:8px;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;color:#BFA164;font-size:16px;flex-shrink:0}
.tp-ref{background:rgba(191,161,100,.08);color:#BFA164;padding:1px 6px;border-radius:4px;font-family:monospace;font-size:10px}

/* Modal */
.tp-overlay{position:fixed;inset:0;background:rgba(0,0,0,.75);z-index:500;display:flex;align-items:center;justify-content:center;padding:16px;overflow-y:auto}
.tp-modal{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;width:100%;max-width:620px;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.5)}
.tp-modal-hd{display:flex;align-items:flex-start;justify-content:space-between;padding:18px 20px;border-bottom:1px solid var(--fm-br,#1E1E1E)}
.tp-modal-bd{padding:18px 20px;overflow-y:auto;flex:1}
.tp-modal-ft{display:flex;justify-content:flex-end;gap:8px;padding:14px 20px;border-top:1px solid var(--fm-br,#1E1E1E)}
.tp-x{background:transparent;border:none;color:var(--fm-m,#A09888);font-size:26px;line-height:1;cursor:pointer;padding:0 4px}
.tp-x:hover{color:#FF6B6B}
.tp-sect{color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin:0 0 8px}
.tp-lbl{display:block;color:var(--fm-m,#A09888);font-size:11px;font-weight:600;margin-bottom:4px}
.tp-inp{width:100%;padding:8px 10px;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;color:var(--fm-h,#F5F0E8);font-size:13px;outline:none;transition:border-color .15s}
.tp-inp:focus{border-color:#BFA164}
.tp-alert{background:rgba(255,107,107,.1);border:1px solid rgba(255,107,107,.3);color:#FF6B6B;padding:8px 12px;border-radius:6px;font-size:12px;margin-bottom:12px}
.tp-summary{background:rgba(191,161,100,.05);border:1px solid rgba(191,161,100,.2);border-left:3px solid #BFA164;border-radius:6px;padding:12px 14px;font-size:12px;color:var(--fm-m,#A09888);line-height:1.5}
.tp-gps-btn{width:40px;height:40px;display:flex;align-items:center;justify-content:center;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;color:#BFA164;cursor:pointer;flex-shrink:0}
.tp-gps-btn:hover:not(:disabled){background:rgba(191,161,100,.12);border-color:#BFA164}
.tp-gps-btn:disabled{opacity:.5;cursor:not-allowed}
.tp-loc-dropdown{position:absolute;left:0;right:48px;top:100%;background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;max-height:240px;overflow-y:auto;z-index:10;box-shadow:0 8px 20px rgba(0,0,0,.4);margin-top:4px}
.tp-loc-item{display:flex;align-items:flex-start;gap:8px;padding:10px 12px;cursor:pointer;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.tp-loc-item:hover{background:rgba(191,161,100,.08)}
.tp-loc-item:last-child{border-bottom:none}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .tp-container { padding-top: 70px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .tp-card { padding: 12px !important; border-radius: 10px !important; }
  .tp-gps-btn { width: 38px; }
  .tp-loc-dropdown { max-height: 200px; }
  .tp-loc-item { padding: 8px 10px; font-size: 12px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "TransporterProfile",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,
    profile:null,vehicles:[],
    showBookModal:false,submitting:false,bookError:'',
    book:{animalCount:null,animalTypeDescription:'',estimatedTotalWeightKg:null,orderId:'',pickupAddress:'',pickupLatitude:null,pickupLongitude:null,pickupContactName:'',pickupContactPhone:'',deliveryAddress:'',deliveryLatitude:null,deliveryLongitude:null,deliveryContactName:'',deliveryContactPhone:'',scheduledPickupDate:'',scheduledPickupTime:'',specialInstructions:''},
    activeSearch:null,pickupResults:[],deliveryResults:[],locSearchTimer:null,gpsLoading:null
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    animalTypes(){
      var a=this.profile&&this.profile.animal_types_served;
      if(Array.isArray(a)) return a;
      if(typeof a==='string'){try{return JSON.parse(a||'[]')}catch(e){return []}}
      return [];
    },
    todayISO(){return new Date().toISOString().split('T')[0]}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';
    this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadProfile();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect();if(this.locSearchTimer)clearTimeout(this.locSearchTimer)},
  watch:{
    '$route.params.transporterId'(){this.loadProfile()}
  },
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    async loadProfile(){
      this.loading=true;this.profile=null;this.vehicles=[];
      var id=this.$route.params.transporterId;
      if(!id){this.loading=false;return}
      try{
        var r=await executeJson({function:'getTransporterProfile',transporterUserId:id},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.profile=r.data.data.profile||null;
          this.vehicles=r.data.data.vehicles||[];
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },

    // ===== Booking modal =====
    openBookModal(){
      var userId=sessionStorage.getItem('userId');
      if(!userId){
        sessionStorage.setItem('flockr_pending_transporter', this.profile.user_id);
        this.$router.push('/login');
        return;
      }
      this.bookError='';
      this.book={animalCount:1,animalTypeDescription:'',estimatedTotalWeightKg:null,orderId:'',pickupAddress:'',pickupLatitude:null,pickupLongitude:null,pickupContactName:'',pickupContactPhone:'',deliveryAddress:'',deliveryLatitude:null,deliveryLongitude:null,deliveryContactName:'',deliveryContactPhone:'',scheduledPickupDate:'',scheduledPickupTime:'',specialInstructions:''};
      this.pickupResults=[];this.deliveryResults=[];this.activeSearch=null;
      this.showBookModal=true;
    },
    closeBookModal(){
      if(this.submitting)return;
      this.showBookModal=false;this.bookError='';this.activeSearch=null;
    },

    // ===== Location search (shared for pickup + delivery) =====
    onSearch(which){
      if(this.locSearchTimer)clearTimeout(this.locSearchTimer);
      var self=this;
      var q = which==='pickup' ? this.book.pickupAddress : this.book.deliveryAddress;
      this.activeSearch=which;
      if(!q || String(q).trim().length<3){
        if(which==='pickup') this.pickupResults=[]; else this.deliveryResults=[];
        return;
      }
      this.locSearchTimer=setTimeout(function(){self.doSearch(which, String(q).trim())},400);
    },
    async doSearch(which,q){
      try{
        var url='https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=6&q='+encodeURIComponent(q);
        var res=await fetch(url);var data=await res.json();
        var mapped=data.map(function(d){var parts=(d.display_name||'').split(',');return{display_name:parts.slice(0,3).join(', ').trim(),area:parts.slice(1,4).join(', ').trim(),lat:d.lat,lon:d.lon}});
        if(which==='pickup') this.pickupResults=mapped; else this.deliveryResults=mapped;
      }catch(e){if(which==='pickup') this.pickupResults=[]; else this.deliveryResults=[]}
    },
    pickLocation(which,loc){
      if(which==='pickup'){
        this.book.pickupAddress=loc.display_name;
        this.book.pickupLatitude=Number(loc.lat);this.book.pickupLongitude=Number(loc.lon);
        this.pickupResults=[];
      } else {
        this.book.deliveryAddress=loc.display_name;
        this.book.deliveryLatitude=Number(loc.lat);this.book.deliveryLongitude=Number(loc.lon);
        this.deliveryResults=[];
      }
      this.activeSearch=null;
    },
    useMyLocation(which){
      if(!navigator.geolocation)return;
      this.gpsLoading=which;
      var self=this;
      navigator.geolocation.getCurrentPosition(
        function(pos){
          var lat=pos.coords.latitude, lon=pos.coords.longitude;
          if(which==='pickup'){self.book.pickupLatitude=lat;self.book.pickupLongitude=lon}
          else {self.book.deliveryLatitude=lat;self.book.deliveryLongitude=lon}
          // Reverse geocode
          fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat='+lat+'&lon='+lon)
            .then(function(r){return r.json()})
            .then(function(d){
              if(d && d.display_name){
                var parts=d.display_name.split(',');var addr=parts.slice(0,3).join(',').trim();
                if(which==='pickup') self.book.pickupAddress=addr; else self.book.deliveryAddress=addr;
              }
              self.gpsLoading=null;
            })
            .catch(function(){self.gpsLoading=null});
        },
        function(){self.gpsLoading=null},
        {enableHighAccuracy:true,timeout:10000}
      );
    },

    async submitBooking(){
      this.bookError='';
      var userId=sessionStorage.getItem('userId');
      if(!userId){this.$router.push('/login');return}
      // Validation
      if(!this.book.animalCount || Number(this.book.animalCount)<1){this.bookError='Animal count is required';return}
      if(!this.book.pickupAddress || !this.book.pickupLatitude){this.bookError='Pickup address is required — pick one from the search list or use GPS';return}
      if(!this.book.deliveryAddress || !this.book.deliveryLatitude){this.bookError='Delivery address is required — pick one from the search list or use GPS';return}
      if(!this.book.scheduledPickupDate){this.bookError='Pickup date is required';return}

      this.submitting=true;
      try{
        var payload={
          function:'bookTransport',
          requesterId:userId,
          transporterUserId:this.profile.user_id,
          animalCount:this.book.animalCount,
          animalTypeDescription:this.book.animalTypeDescription||'',
          estimatedTotalWeightKg:this.book.estimatedTotalWeightKg||'',
          orderId:this.book.orderId||'',
          pickupAddress:this.book.pickupAddress,
          pickupLatitude:this.book.pickupLatitude,
          pickupLongitude:this.book.pickupLongitude,
          pickupContactName:this.book.pickupContactName||'',
          pickupContactPhone:this.book.pickupContactPhone||'',
          deliveryAddress:this.book.deliveryAddress,
          deliveryLatitude:this.book.deliveryLatitude,
          deliveryLongitude:this.book.deliveryLongitude,
          deliveryContactName:this.book.deliveryContactName||'',
          deliveryContactPhone:this.book.deliveryContactPhone||'',
          scheduledPickupDate:this.book.scheduledPickupDate,
          scheduledPickupTime:this.book.scheduledPickupTime||'',
          specialInstructions:this.book.specialInstructions||''
        };
        var r=await executeJson(payload,flocker);
        if(r.data && r.data.success){
          var bookingId = r.data.data && r.data.data.bookingId;
          this.showBookModal=false;
          if(bookingId){this.$router.push('/transport/trip/'+bookingId)}
          else {alert('Request sent successfully.')}
        } else {
          this.bookError=(r.data&&r.data.message)||'Failed to send request';
        }
      }catch(e){
        this.bookError=(e.response&&e.response.data&&e.response.data.message)||'Network error. Please try again.';
      }
      this.submitting=false;
    }
  }
};
</script>
